interface RunWhenIdleOptions {
  /** Upper bound (ms) for requestIdleCallback once the page has loaded. */
  timeout?: number;
}

const DEFAULT_IDLE_TIMEOUT_MS = 3000;
// Safari has no requestIdleCallback; give the main thread a short breather after load instead.
const FALLBACK_DELAY_MS = 1000;

/**
 * Runs `callback` after the window `load` event, once the browser is idle.
 * Used to start non-critical third-party SDKs without competing with the first render.
 * Returns a cancel function.
 */
export function runWhenIdle(callback: () => void, { timeout = DEFAULT_IDLE_TIMEOUT_MS }: RunWhenIdleOptions = {}) {
  if (typeof window === 'undefined') {
    return () => {};
  }

  let cancelScheduled: (() => void) | null = null;

  const schedule = () => {
    if (typeof window.requestIdleCallback === 'function') {
      const id = window.requestIdleCallback(callback, { timeout });
      cancelScheduled = () => window.cancelIdleCallback(id);
      return;
    }

    const id = window.setTimeout(callback, FALLBACK_DELAY_MS);
    cancelScheduled = () => window.clearTimeout(id);
  };

  if (document.readyState === 'complete') {
    schedule();
  } else {
    window.addEventListener('load', schedule, { once: true });
  }

  return () => {
    window.removeEventListener('load', schedule);
    cancelScheduled?.();
  };
}
